/**
 * Fund–vehicle × quarter grid: CAS presence, drawdown/distribution rows when CAS reports activity.
 */
import { Link } from 'react-router-dom'
import { Check, X } from 'lucide-react'
import { useAuth } from '../Auth/AuthContext'
import { usePortfolioData } from '../../hooks/usePortfolioData'
import {
  buildCoverageMatrix,
  quarterLabel,
  CAS_AMOUNT_EPSILON,
} from '../../lib/documentCoverage'

const btnPrimary =
  'px-4 py-2 bg-brand hover:opacity-90 text-ink-onAccent rounded-lg text-sm transition'

function CoverageCell({ cell }) {
  if (cell.status === 'na') {
    return (
      <div className="flex flex-col items-center justify-center min-w-[4.5rem] py-1 text-ink-subtle text-sm">
        —
      </div>
    )
  }

  const casIcon =
    cell.casOk === true ? (
      <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
    ) : (
      <X className="w-4 h-4 text-danger-fg" strokeWidth={2.5} />
    )

  function subGlyph(ok) {
    if (ok === null) {
      return <span className="text-ink-subtle text-[10px]">—</span>
    }
    return ok ? (
      <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
    ) : (
      <X className="w-3 h-3 text-danger-fg" strokeWidth={2.5} />
    )
  }

  return (
    <div className="flex flex-col items-center justify-center gap-0.5 min-w-[4.5rem] py-1 border-l border-edge-subtle/80">
      <div className="flex items-center justify-center" title="CAS statement">
        {casIcon}
      </div>
      <div className="flex items-center gap-1.5 text-[10px] text-ink-muted leading-none">
        <span className="flex items-center gap-0.5" title="Drawdown detail (when CAS shows capital called)">
          <span className="opacity-80">DD</span>
          {subGlyph(cell.drawdownOk)}
        </span>
        <span className="flex items-center gap-0.5" title="Distribution detail (when CAS shows distributions)">
          <span className="opacity-80">Dist</span>
          {subGlyph(cell.distOk)}
        </span>
      </div>
    </div>
  )
}

export default function DocumentCoverage() {
  const { currentOrg, orgError, refreshOrgs } = useAuth()
  const orgId = currentOrg?.id
  const { data, fundVariables, loading, error, refresh } = usePortfolioData(orgId)

  if (orgError) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center max-w-md">
          <div className="text-warning-fg text-xl mb-2">Could not load organization</div>
          <div className="text-ink-subtle text-sm mb-4">{orgError}</div>
          <button type="button" onClick={refreshOrgs} className={btnPrimary}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="text-ink-muted">Loading coverage data...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center max-w-md">
          <div className="text-danger-fg text-xl mb-2">Failed to load data</div>
          <div className="text-ink-subtle text-sm mb-4">{error}</div>
          <button type="button" onClick={refresh} className={btnPrimary}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!data?.funds?.length) {
    return (
      <div className="app-container py-8">
        <div className="text-center max-w-md mx-auto">
          <div className="text-ink-muted text-xl mb-2">No portfolio data yet</div>
          <div className="text-ink-subtle text-sm mb-4">
            Add fund investments and import CAS statements to see document coverage.
          </div>
          <Link to="/import" className={`inline-block px-6 py-2 ${btnPrimary}`}>
            Import data
          </Link>
        </div>
      </div>
    )
  }

  const matrix = buildCoverageMatrix({
    funds: data.funds,
    cas_timeline: data.cas_timeline || [],
    drawdowns: data.drawdowns || [],
    distributions: data.distributions || [],
    fundVariables,
  })

  return (
    <div className="app-container py-6 stack-lg">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">Document coverage</h1>
          <p className="text-sm text-ink-muted mt-1 max-w-2xl">
            Quarters before each fund&apos;s{' '}
            <strong className="font-medium text-ink">First investment year</strong> or{' '}
            <strong className="font-medium text-ink">Vintage year</strong> (from Fund Variables), or before
            the first CAS on file, are not required. Drawdown and distribution columns are only checked when a
            CAS for that quarter reports capital called or distributions (above {CAS_AMOUNT_EPSILON}).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={refresh} className="px-3 py-1.5 text-sm rounded-lg border border-edge text-ink-muted hover:text-ink hover:bg-surface-tertiary transition">
            Refresh
          </button>
          <Link
            to="/import"
            className="px-3 py-1.5 text-sm rounded-lg bg-surface-tertiary text-ink hover:opacity-90 transition"
          >
            Import
          </Link>
        </div>
      </div>

      <div className="rounded-lg border border-edge bg-surface-secondary p-4 stack-md">
        <div className="text-sm text-ink-muted">
          <span className="inline-flex items-center gap-1 mr-4">
            <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
            OK
          </span>
          <span className="inline-flex items-center gap-1 mr-4">
            <X className="w-3.5 h-3.5 text-danger-fg" strokeWidth={2.5} />
            Missing
          </span>
          <span className="text-ink-subtle">— Not applicable (before coverage start)</span>
        </div>
        <p className="text-xs text-ink-subtle">
          Detail rows are matched by calendar quarter of transaction date; notices dated just outside the CAS
          quarter may show a false gap until matching is relaxed.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-edge bg-surface-secondary shadow-sm" style={{ WebkitOverflowScrolling: 'touch' }}>
        <table className="text-sm w-full border-collapse">
          <thead>
            <tr className="bg-surface-tertiary/80">
              <th className="sticky left-0 z-20 bg-surface-tertiary border-b border-edge px-2 sm:px-3 py-2 text-left font-medium text-ink w-[10rem] sm:w-[14rem] min-w-[10rem] sm:min-w-[14rem] max-w-[10rem] sm:max-w-[14rem] shadow-[4px_0_8px_-4px_rgba(0,0,0,0.12)]">
                <div>Fund</div>
                <div className="text-xs font-normal text-ink-muted mt-0.5">Investor / vehicle</div>
              </th>
              {matrix.quarters.map((yq) => (
                <th
                  key={`${yq.year}-${yq.q}`}
                  className="border-b border-edge px-1 py-2 text-center font-medium text-ink-muted whitespace-nowrap min-w-[4.5rem]"
                >
                  {quarterLabel(yq)}
                </th>
          ))}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((row) => (
              <tr key={row.key} className="border-b border-edge-subtle last:border-b-0 hover:bg-surface-tertiary/40">
                <td className="sticky left-0 z-10 bg-surface-secondary px-2 sm:px-3 py-2 align-top border-r border-edge-subtle shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)] w-[10rem] sm:w-[14rem] min-w-[10rem] sm:min-w-[14rem] max-w-[10rem] sm:max-w-[14rem]">
                  <div className="font-medium text-ink break-words">{row.fund}</div>
                  <div className="text-ink-muted text-xs mt-1 break-words">{row.investor}</div>
                </td>
                {row.cells.map((cell) => (
                  <td key={cell.quarterKey} className="align-top bg-surface-secondary p-0">
                    <CoverageCell cell={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
